import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, MessagesSquare, UserCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { AppLangToggle } from "@/components/AppLangToggle";

const HERO = "https://static.prod-images.emergentagent.com/jobs/3085e34f-2862-4361-89ba-1e9784a7f039/images/5acd6e13d85a9884c3dde695422beff4a3902e54a6323db486a5c0be168b2c17.jpeg";
const MARK = {
  WebkitMaskImage: "url(/melletic-mark.png)", maskImage: "url(/melletic-mark.png)",
  WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
  WebkitMaskPosition: "center", maskPosition: "center",
  background: "linear-gradient(135deg,#bcddff 0%,#6aa6f7 30%,#2f6fe0 62%,#17368f 100%)",
  filter: "drop-shadow(0 4px 8px rgba(23,42,110,0.32))",
};

export default function Landing() {
  const nav = useNavigate();
  const { t } = useLang();
  const FEATURES = [
    { icon: BookOpen, t: t("feat_structured"), s: t("feat_structured_s") },
    { icon: MessagesSquare, t: t("feat_practice"), s: t("feat_practice_s") },
    { icon: UserCheck, t: t("feat_built"), s: t("feat_built_s") },
  ];
  return (
    <div className="min-h-screen w-full flex justify-center bg-[#F8FAFC]">
      <div className="relative w-full max-w-[440px] min-h-screen flex flex-col px-6 pt-10 pb-7 overflow-hidden"
        style={{ background: "linear-gradient(180deg,#eef4fc 0%,#F8FAFC 40%)" }}>
        {/* interface-language selector (controls ALL text before login) */}
        <div className="absolute right-5 top-5 z-20"><AppLangToggle /></div>
        {/* brand */}
        <div className="flex flex-col items-center animate-float-up">
          <div style={{ ...MARK, width: 96, height: 96 * (243 / 490) * 1.9 }} className="block" data-testid="landing-mark" />
          <p className="font-heading font-extrabold text-[#0F172A] mt-1" style={{ letterSpacing: "0.34em", fontSize: 22, paddingLeft: "0.34em" }}>MELLETIC</p>
          <p className="mt-1.5 text-[15px] font-semibold">
            <span className="text-[#8a94a6]">{t("tagline_a")} </span>
            <span style={{ background: "linear-gradient(100deg,#2563eb,#22aee8)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{t("tagline_b")}</span>
          </p>
        </div>

        {/* headline */}
        <div className="mt-6 animate-float-up" style={{ animationDelay: "0.05s" }}>
          <h1 className="font-heading font-extrabold text-[#0F172A]" style={{ fontSize: "clamp(28px,8vw,36px)", lineHeight: 1.12 }}>
            {t("land_headline_a")}<br />{t("land_headline_b")}<br />{t("land_headline_c")} <span style={{ background: "linear-gradient(100deg,#2563eb,#22aee8)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{t("land_live")}</span>
          </h1>
          <p className="text-[#5b6472] mt-3 text-[15px] leading-relaxed max-w-[330px]">
            {t("land_sub")}
          </p>
        </div>

        {/* cinematic hero image */}
        <div className="mt-5 rounded-[26px] overflow-hidden shadow-[0_26px_54px_-28px_rgba(15,23,42,0.55)] animate-float-up" style={{ animationDelay: "0.1s" }}>
          <img src={HERO} alt="" className="w-full h-[248px] object-cover" />
        </div>

        {/* value blocks */}
        <div className="grid grid-cols-3 gap-3 mt-6 animate-float-up" style={{ animationDelay: "0.15s" }}>
          {FEATURES.map(({ icon: Icon, t, s }) => (
            <div key={t} className="flex flex-col items-center text-center">
              <span className="w-12 h-12 rounded-2xl bg-[#E0F2FE] flex items-center justify-center"><Icon size={20} className="text-[#2563EB]" /></span>
              <p className="text-[12px] font-bold text-[#0F172A] mt-2 leading-tight">{t}</p>
              <p className="text-[11px] text-[#64748B] leading-tight mt-0.5">{s}</p>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-6 animate-float-up" style={{ animationDelay: "0.2s" }}>
          <button data-testid="get-started-btn" onClick={() => nav("/register")}
            className="w-full flex items-center justify-center gap-2 font-bold text-[17px] py-4 rounded-[26px] active:scale-[0.99] transition-transform"
            style={{ color: "#ffffff", background: "linear-gradient(100deg,#1d4ed8 0%,#2563eb 50%,#22aee8 100%)", boxShadow: "0 18px 40px -14px rgba(37,99,235,0.6)" }}>
            <span style={{ color: "#ffffff" }}>{t("land_get_started")}</span> <ArrowRight size={19} style={{ color: "#ffffff" }} />
          </button>
          <button data-testid="login-link" onClick={() => nav("/login")} className="w-full py-3.5 mt-1 text-[#2563EB] font-semibold text-[15px]">
            {t("land_have_account")}
          </button>
        </div>
      </div>
    </div>
  );
}
